import { useSearchParams } from 'next/navigation';
import { useQuery } from 'react-query';
import { QueryId, STALE_TIME } from '@/integrations/content/utils';
import {
  TriggeredByType,
  useSearchContext,
} from '@/libraries/contexts/search-context';
import getCulture from '@/libraries/getters/get-culture';
import getLocale from '@/libraries/getters/get-locale';

type UseQueryHotelsWithOffersBestOffersData =
  Offers.Endpoints.HotelsWithOffersBestOffers;

interface UseQueryHotelsWithOffersBestOffersOptions {
  initialData?: UseQueryHotelsWithOffersBestOffersData;
  triggeredBy?: TriggeredByType;
}

interface UseQueryHotelsWithOffersBestOffersParameters {
  brand?: Content.Properties.Brand;
}

const useQueryHotelsWithOffersBestOffers = (
  parameters: UseQueryHotelsWithOffersBestOffersParameters,
  options?: UseQueryHotelsWithOffersBestOffersOptions,
) => {
  const locale = getLocale();
  const searchParams = useSearchParams();
  const { bonusCodes } = useSearchContext();

  const query = useQuery<UseQueryHotelsWithOffersBestOffersData>(
    [
      QueryId.HOTELS_WITH_OFFERS_BEST_OFFERS,
      parameters,
      locale,
      searchParams!.get('adultsNumber'),
      searchParams!.get('adultsPerRoom'),
      bonusCodes,
      searchParams!.get('childrenAges'),
      searchParams!.get('childrenAgesPerRoom'),
      searchParams!.get('departureDate'),
      searchParams!.get('destinationIds'),
      searchParams!.get('destinationRegionIds'),
      searchParams!.get('durationRange'),
      searchParams!.get('durationType'),
      searchParams!.get('hotelIds'),
      searchParams!.get('filterDiscount'),
      searchParams!.get('filterFacilityIds'),
      searchParams!.get('filterFacilityValues'),
      searchParams!.get('filterStars'),
    ],
    async () => {
      const baseUrl = '/api/hotels-with-offers/best-offers';

      const searchParameters = new URLSearchParams({
        departureDate: String(searchParams!.get('departureDate')),
        departureIds: String(searchParams!.get('departureIds')),
        destinationIds: String(searchParams!.get('destinationIds')),
        durationType: String(searchParams!.get('durationType')),
      });

      if (parameters.brand) {
        searchParameters.append('brand', String(parameters.brand));
      }

      if (searchParams!.get('adultsNumber')) {
        searchParameters.append(
          'adultsNumber',
          String(searchParams!.get('adultsNumber')),
        );
      }

      if (searchParams!.get('adultsPerRoom')) {
        searchParameters.append(
          'adultsPerRoom',
          String(searchParams!.get('adultsPerRoom')),
        );
      }

      if (searchParams!.get('childrenAges')) {
        searchParameters.append(
          'childrenAges',
          String(searchParams!.get('childrenAges')),
        );
      }

      if (bonusCodes) {
        searchParameters.append('bonusCodes', String(bonusCodes));
      }

      if (searchParams!.get('childrenAgesPerRoom')) {
        searchParameters.append(
          'childrenAgesPerRoom',
          String(searchParams!.get('childrenAgesPerRoom')),
        );
      }

      if (searchParams!.get('destinationRegionIds')) {
        searchParameters.append(
          'destinationRegionIds',
          String(searchParams!.get('destinationRegionIds')),
        );
      }

      if (searchParams!.get('durationRange')) {
        searchParameters.append(
          'durationRange',
          String(searchParams!.get('durationRange')),
        );
      }

      if (searchParams!.get('filterDiscount')) {
        searchParameters.append(
          'filterDiscount',
          String(searchParams!.get('filterDiscount')),
        );
      }

      if (searchParams!.get('filterFacilityIds')) {
        searchParameters.append(
          'filterFacilityIds',
          String(searchParams!.get('filterFacilityIds')),
        );
      }

      if (searchParams!.get('filterFacilityValues')) {
        searchParameters.append(
          'filterFacilityValues',
          String(searchParams!.get('filterFacilityValues')),
        );
      }

      if (searchParams!.get('filterStars')) {
        searchParameters.append(
          'filterStars',
          String(searchParams!.get('filterStars')),
        );
      }

      if (searchParams!.get('hotelIds')) {
        searchParameters.append(
          'hotelIds',
          String(searchParams!.get('hotelIds')),
        );
      }

      const fetchResponse = await fetch(
        `${baseUrl}?${searchParameters.toString()}`,
        {
          headers: {
            ['Accept-Language']: getCulture(locale),
            ['Content-Type']: 'application/json',
          },
          method: 'GET',
        },
      )
        .then(response => {
          if (response.ok) return response.json();

          throw new Error(`${response.status} - ${response.statusText}`);
        })
        .catch(error => {
          console.error('hooks.useQueryHotelsWithOffersBestOffers', error);

          throw error;
        });

      return fetchResponse;
    },
    {
      enabled:
        !!searchParams!.get('departureDate') &&
        (!!searchParams!.get('adultsNumber') ||
          !!searchParams!.get('adultsPerRoom')) &&
        !!searchParams!.get('departureIds') &&
        !!searchParams!.get('durationType') &&
        options?.triggeredBy !== 'calendarSlider',
      initialData: options?.initialData,
      keepPreviousData: true,
      staleTime: STALE_TIME,
    },
  );

  return query;
};

export default useQueryHotelsWithOffersBestOffers;
